import React, { useEffect, useState } from "react";
import PropTypes from "prop-types"; // Import PropTypes
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LabelList,
    Cell,
} from "recharts";
import Loader from "../components/common/loader";
import CustomPaper from "../components/common/customPaper";
import { Box, Button } from "@mui/material";
import { COLORS } from "../utils/constant";
import NoDataAvailable from "../components/common/NoDataAvailable";
import FilterableContentPanel from "../components/layout/FilterableContentPanel";
import { useSelector } from "react-redux";
import {
    useGetPerformanceSectorsQuery,
    useGetSectorPublicationsQuery,
} from "../redux/service/modules/dashboard";
import { useResponsiveStyles } from "../styles/useResponsiveStyles";

const SectorBarChart = ({ isLoading, error }) => {
    const dateRange = useSelector((state) => state.date);
    const [filters, setFilters] = useState([]);
    const { getFontSize, getContainerHeight } = useResponsiveStyles();
    const [showViewAll, setShowViewAll] = useState(false);
    const [filterDetails, setFilterDetails] = useState({
        appliedFilters: {},
        filterCount: 0,
        rawFilters: {},
    });
    const [isViewAllOpen, toggleViewAll] = useState(false);
    const { data: sectorData } = useGetSectorPublicationsQuery({
        ...filterDetails.appliedFilters,
    });

    useEffect(() => {
        if (sectorData?.sectors) {
            const sectorFilter = {
                name: "sectorName",
                label: "Sector",
                key: "sector",
                options: sectorData.sectors.map((sector) => ({
                    label: sector,
                    id: sector,
                })),
                multiSelect: true,
            };
            setFilters((prevFilters) => {
                const filtersCopy = [...prevFilters];
                const existingFilterIndex = filtersCopy.findIndex(
                    (f) => f.name === "sectorName"
                );
                if (existingFilterIndex >= 0) {
                    filtersCopy[existingFilterIndex] = sectorFilter;
                } else {
                    filtersCopy.push(sectorFilter);
                }
                return filtersCopy;
            });
        }
    }, [sectorData]);

    const FilterableElm = ({ appliedFilters, filterElmDateRange }) => {
        return (
            <Box sx={styles.actionPanelBox}>
                {/* <Box sx={styles.panelWidth}> */}
                <HorizontalBarChart
                    height={"100%"}
                    appliedFilters={appliedFilters}
                    filterElmDateRange={filterElmDateRange}
                    showView={() => {
                        setShowViewAll(true);
                    }}
                    barSize={40}
                    isShort={true}
                />
                {/* </Box> */}
            </Box>
        );
    };

    const closeDrawer = () => {
        toggleViewAll(false);
    };

    return (
        <CustomPaper sx={getContainerHeight("sectorial-paper")}>
            <h2
                className="font-bold text-primary mb-3 text-start pt-0.5"
                style={getFontSize("sub-heading")}
            >
                Sectorial Coverage
            </h2>
            <>
                <HorizontalBarChart
                    height={"81%"}
                    maxBars={7}
                    showView={() => {
                        setShowViewAll(true);
                    }}
                    isShort={false}
                />
                {showViewAll && (
                    <div className="flex justify-end">
                        <Button
                            variant="text"
                            color="primary"
                            className="view-all-button"
                            // className="absolute"
                            disableRipple
                            // sx={styles.viewAllBtn}
                            onClick={() => toggleViewAll(true)}
                        >
                            View all
                        </Button>
                    </div>
                )}
                <FilterableContentPanel
                    title={"Sectorial Coverage"}
                    filterableElm={FilterableElm}
                    filtersContent={filters}
                    isPanelOpen={isViewAllOpen}
                    closeDrawer={closeDrawer}
                    initialDateRange={dateRange}
                    isShowDateRangeFilter={true}
                />
            </>
        </CustomPaper>
    );
};

SectorBarChart.propTypes = {
    sectors: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.string,
};

const styles = {
    staticContent: {
        height: "80%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
    },
    actionPanel: {
        height: "100%",
        p: 3,
        bgcolor: "#FFFFFF",
        color: "#C43A29",
    },
    actionPanelBox: {
        display: "flex",
        height: "100%",
        bgcolor: "white",
        width: "800px",
    },
};

const HorizontalBarChart = ({
    height,
    barSize = 60,
    appliedFilters,
    filterElmDateRange,
    maxBars,
    showView,
    isShort,
}) => {
    const dateRange = useSelector((state) => state.date);
    const { getFontSize } = useResponsiveStyles();
    const {
        data: sectors,
        isLoading,
        error,
    } = useGetPerformanceSectorsQuery({ ...dateRange, appliedFilters });
    const [data, setData] = useState([]);

    const convertData = (input) => {
        return Object.entries(input).map(([sector, value]) => ({
            sector,
            value,
        }));
    };
    useEffect(() => {
        if (sectors) {
            const convertedData = convertData(sectors);
            const limitedData = maxBars
                ? convertedData.slice(0, maxBars)
                : convertedData;
            setData(limitedData);
        }
    }, [sectors]);

    useEffect(() => {
        if (data?.length === 0) {
            showView();
        }
    }, [data]);

    let staticContent;
    if (isLoading) {
        staticContent = <Loader />;
    } else if (error) {
        staticContent = "Error";
    } else if (data?.length === 0) {
        staticContent = <NoDataAvailable />;
    }
    const mHeight = isShort ? Math.max(data.length * barSize, 200) : height;

    return (
        <>
            {staticContent ? (
                <div style={styles.staticContent}>{staticContent}</div>
            ) : (
                <ResponsiveContainer
                    height={mHeight}
                    className={"overflowY"}
                    style={{ marginRight: 10 }}
                >
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{ left: 0 }}
                        barSize={barSize}
                    >
                        <XAxis
                            type="number"
                            axisLine={false}
                            tickLine={false}
                            tick={false}
                            reversed={true}
                        />
                        <YAxis
                            type="category"
                            dataKey="sector"
                            tick={{
                                ...getFontSize("chart"),
                                fill: "#231F20",
                                fontWeight: "semi-bold",
                                textAnchor: "start",
                                dx: -160,
                            }}
                            axisLine={false}
                            tickLine={false}
                            orientation="left"
                            width={170}
                            reversed={false}
                            interval={0}
                        />
                        <Tooltip
                            formatter={(value) =>
                                new Intl.NumberFormat().format(value)
                            }
                            cursor={{ fill: "transparent" }}
                        />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                            {data.map((entry, index) => (
                                <Cell
                                    key={entry.sector}
                                    fill={COLORS[index % COLORS.length]}
                                />
                            ))}
                            <LabelList
                                dataKey="value"
                                position="insideRight"
                                fill="#000000"
                                formatter={(value) =>
                                    new Intl.NumberFormat().format(value)
                                }
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </>
    );
};

HorizontalBarChart.propTypes = {
    data: PropTypes.array.isRequired,
    height: PropTypes.string.isRequired,
};

export default SectorBarChart;
